import { useCallback, useMemo } from "react";

import Save from "@mui/icons-material/Save";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";

import Address from "./Address";
import type { AddressFieldsConfig, AddressFormValues } from "./Address.types";
import { locationAddressSchema } from "./address.utils";

export type AddressFormProps = {
  value: AddressFormValues;
  fieldsConfig: AddressFieldsConfig;
  onSave: (value: AddressFormValues) => void | Promise<void>;
  saving?: boolean;
  disabled?: boolean;
  disabledHint?: string;
};

const AddressForm = ({
  value,
  fieldsConfig,
  onSave,
  saving = false,
  disabled = false,
  disabledHint,
}: AddressFormProps) => {
  const { t } = useTranslation(["common", "yup"]);

  const stateRequired = fieldsConfig?.region?.required === true;
  const validationSchema = useMemo(() => locationAddressSchema(t, { stateRequired }), [stateRequired, t]);

  const formik = useFormik<AddressFormValues>({
    initialValues: value,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      await onSave(values);
    },
  });

  const busy = saving || formik.isSubmitting;

  const handleChange = useCallback(
    (next: AddressFormValues) => {
      void formik.setValues(next);
    },
    [formik],
  );

  const handleCancel = useCallback(() => formik.resetForm(), [formik]);

  return (
    <form onSubmit={formik.handleSubmit}>
      <Stack spacing={2}>
        <Address
          value={formik.values}
          onChange={handleChange}
          disabled={disabled || busy}
          fieldsConfig={fieldsConfig}
        />

        {disabled && disabledHint && (
          <Typography variant="body2" color="text.secondary">
            {disabledHint}
          </Typography>
        )}

        {!disabled && (
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button variant="text" disabled={!formik.dirty || busy} onClick={handleCancel}>
              {t("common:cancel")}
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={!formik.dirty || !formik.isValid || formik.isValidating || busy}
              startIcon={<Save />}
            >
              {busy ? t("common:saving") : t("common:save")}
            </Button>
          </Stack>
        )}
      </Stack>
    </form>
  );
};

export default AddressForm;
